<template>
    <back-to-home />
    <page-title :title="t('register.registerTitle')" />
    <v-card-text>
        <v-form ref="form" @submit.prevent="handleSubmit">
            <form-section :is-loading="isLoading">
                <field-group>
                    <input-field
                        id="FirstName"
                        v-model="formData.firstName"
                        :disabled="isSubmitting"
                        :required="true"
                        :label="t('register.firstName')"
                    />
                    <input-field
                        id="LastName"
                        v-model="formData.lastName"
                        :disabled="isSubmitting"
                        :required="true"
                        :label="t('register.lastName')"
                    />
                </field-group>
            </form-section>

            <form-section :is-loading="isLoading">
                <phone-field id="profile-phone" v-model="formData.phone" :label="t('register.phoneNumber')" :disabled="isSubmitting" />
            </form-section>

            <form-section :is-loading="isLoading">
                <input-field
                    id="email"
                    v-model="formData.email"
                    :disabled="isSubmitting"
                    :pattern="FIELD_PATTERNS.EMAIL"
                    :pattern-error="t('validation.emailRequired')"
                    :required="true"
                    :label="t('register.email')"
                />
            </form-section>

            <form-section :is-loading="isLoading">
                <input-field
                    id="password"
                    v-model="formData.password"
                    :type="FIELD_TYPES.PASSWORD"
                    :required="true"
                    :pattern="FIELD_PATTERNS.CAPITAL_LETTER"
                    :disabled="isSubmitting"
                    :label="t('register.password')"
                />
            </form-section>

            <form-section :is-loading="isLoading">
                <input-field
                    id="passwordConfirm"
                    v-model="formData.passwordConfirm"
                    :type="FIELD_TYPES.PASSWORD"
                    :reference-value="formData.password"
                    :reference-error-label="t('register.passwordsMustMatch')"
                    :disabled="isSubmitting"
                    :required="true"
                    :label="t('register.passwordConfirm')"
                />
            </form-section>

            <form-section :is-loading="isLoading">
                <checkbox-field
                    id="confirmTerms"
                    v-model="formData.confirmTerms"
                    :required="true"
                    :label="t('register.iAgreeToTerms')"
                    :disabled="isSubmitting"
                />
                <v-card-actions class="d-flex justify-end ma-0 pa-0">
                    <card-button
                        type="submit"
                        class="flex-1-0-0"
                        size="large"
                        variant="flat"
                        :inactive-label="t('inquiry.sendMessage')"
                        :disabled="isSubmitting"
                    >
                        {{ t('register.submit') }}
                    </card-button>
                </v-card-actions>
            </form-section>
        </v-form>
        <notification-bar
            v-model:show="notificationBarState.show"
            :color="notificationBarState.color"
            :icon="notificationBarState.icon"
            :border="notificationBarState.border"
            :message="notificationBarState.message"
        />
    </v-card-text>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { BackToHome, PageTitle } from '@/features/common/components/page';
import { CheckboxField, FieldGroup, FormSection, InputField, NotificationBar, PhoneField } from '@/features/common/components/form';
import { DEFAULT_COUNTRY, FIELD_PATTERNS, FIELD_TYPES, RECAPTCHA_ACTIONS } from '@/features/common/components/form/constants';
import { useFormData, useFormHandling, useNotification } from '@/features/common/components/form/composables';
import CardButton from '@/features/common/components/CardButton.vue';
import { CreateProfileDto, PhoneDto } from '@/types/webapi';
import { registerProfile } from '@/features/Profile/services/api';
import router, { routeNames } from '@/router';
import { i18nRoute } from '@/i18n';

const { t } = useI18n();

const isLoading = ref(false);

interface RegisterFormData {
    firstName: string;
    lastName: string;
    email: string;
    phone: PhoneDto;
    password: string;
    passwordConfirm: string;
    confirmTerms: boolean;
}

const registerData: RegisterFormData = {
    firstName: '',
    lastName: '',
    email: '',
    phone: { countryCode: DEFAULT_COUNTRY.PHONE_CODE, number: '' } as PhoneDto,
    password: '',
    passwordConfirm: '',
    confirmTerms: false
};

const { notificationBarState, notificationError } = useNotification();
const { form, isSubmitting, toggleIsSubmitting, validateForm, isFormEmpty } = useFormHandling();
const { formData, resetFormData, executeRecaptcha } = useFormData(registerData);

const saveData = async (data: RegisterFormData) => {
    try {
        const profileRegister = {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            password: data.password,
            phone: data.phone.number,
            phoneCountryCode: data.phone.countryCode.indexOf('+') > -1 ? data.phone.countryCode : '+' + data.phone.countryCode,
            recaptchaToken: await executeRecaptcha(RECAPTCHA_ACTIONS.CREATE_PROFILE)
        } as CreateProfileDto;

        const createProfileResult = await registerProfile(profileRegister);

        if (createProfileResult.isSuccessful) {
            await router.push(i18nRoute({ name: routeNames.registerSuccess }));
            return true;
        } else {
            notificationBarState.value = notificationError(createProfileResult?.error || t('register.registerError'));
            return false;
        }
    } catch {
        notificationBarState.value = notificationError(t('register.registerError'));
        return false;
    }
};

const submitForm = async () => {
    try {
        toggleIsSubmitting(true);
        const result = await saveData(formData.value);
        result && resetFormData(form);
    } catch {
        notificationBarState.value = notificationError(t('register.genericError'));
    } finally {
        toggleIsSubmitting(false);
    }
};

const handleSubmit = async () => {
    if (isFormEmpty()) return;
    const { valid } = await validateForm();
    if (!valid) return;
    await submitForm();
};
</script>
